/*
  Core configuration files
 */
import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {ExtentConfiguration, GeometryConfiguration} from "./geometry-configuration";
import {
    FillSymbolConfiguration,
    PictureMarkerSymbolConfiguration, SimpleLineSymbolConfiguration,
    SimpleMarkerSymbolConfiguration
} from "./symbol-configuration";
import {PopupTemplateConfiguration} from "./popup-configuration";
import {PortalItemConfiguration} from "./portal-item-configuration";
import {FeatureLayerConfiguration} from "./layer-configuration";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";

@JsonObject()
export class SearchSourceFilterConfiguration {
    /**
     * The where clause specified for filtering suggests or search results.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#filter)
     */
    @JsonProperty()
    declare where?: string;

    /**
     * The filter geometry for suggests or search results.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#filter)
     */
    @JsonProperty({ type: GeometryConfiguration })
    declare geometry?: GeometryConfiguration;

    constructor() {}
}

// A predicate function use to determine what is the symbol type
const symbolTypePredicate = (symbol: any) => {
    if (symbol.type === "picture-marker") {
        return PictureMarkerSymbolConfiguration;
    }
    if (symbol.type === "simple-marker") {
        return SimpleMarkerSymbolConfiguration;
    }
    return SimpleMarkerSymbolConfiguration;
};

// A predicate function use to determine what is the right searchSource type
const searchSourcePredicate = (searchSource: any) =>
    (searchSource["url"] !== undefined  || searchSource["locator"] !== undefined )?
        LocatorSearchSourceConfiguration : (searchSource["maxExtent"] !== undefined)?
            CoordinateSearchSourceConfiguration: LayerSearchSourceConfiguration;


export enum SearchSourceType {
    Locator = "locator",
    Layer = "layer",
    Custom = "custom",
    Coordinate = "coordinate"
}
/**
 * *Geo.js Property*
 * Query for additional data
 */
@JsonObject()
export class SearchSourceQueryConfiguration {
    @JsonProperty()
    declare expression: string;
    @JsonProperty()
    declare id: string;
    @JsonProperty()
    declare layerId?: number;
    @JsonProperty()
    declare outFields?: string[];
    @JsonProperty()
    declare popupTemplate?: PopupTemplateConfiguration | string;
    @JsonProperty()
    declare portalItem?: PortalItemConfiguration;
    @JsonProperty()
    declare title?: string;
    constructor() {}
}
/**
 * *Geo.js Property*
 * Query for additional data
 */
@JsonObject()
export class SearchResultSymbolConfiguration {
    @JsonProperty()
    declare highlight: FillSymbolConfiguration;
    @JsonProperty()
    declare line: SimpleLineSymbolConfiguration;
    @JsonProperty()
    declare pointer: PictureMarkerSymbolConfiguration;
    constructor() {}
}
@JsonObject()
export class SearchSourceConfiguration {
    /**
     * *Geo.js Property*
     * Query for additional data
     */
    @JsonProperty()
    declare name?: string;
    /**
     * *Geo.js Property*
     * Type of search source.
     * For internal use only.
     */
    @JsonProperty()
    declare sourceType: SearchSourceType;
    /**
     * *Geo.js Property*
     * Query for additional data
     */
    @JsonProperty()
    declare query?: SearchSourceQueryConfiguration;
    /**
     * *Geo.js Property*
     * Query for additional data
     */
    @JsonProperty()
    declare queries?: SearchSourceQueryConfiguration[];

    /**
     * Indicates whether to automatically navigate to the selected result once selected.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#autoNavigate)
     */
    @JsonProperty()
    declare autoNavigate?: boolean;

    /**
     * For filtering suggests or search results.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#filter)
     */
    @JsonProperty({ type: SearchSourceFilterConfiguration })
    declare filter?: SearchSourceFilterConfiguration;
    /**
     * Function used to get search results.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#getResults)
     */
    declare getResults?: (params: any) => Promise<__esri.SearchResult[]>;
    /**
     * Function used to get search suggestions.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#getSuggestions)
     */
    declare getSuggestions?: (params: any) => Promise<__esri.SuggestResult[]>;
    /**
     * Indicates the maximum number of search results to return.
     *
     * @default 6
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#maxResults)
     */
    @JsonProperty()
    declare maxResults?: number;

    /**
     * Indicates the maximum number of suggestions to return for the widget's input.
     *
     * @default 6
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#maxSuggestions)
     */
    @JsonProperty()
    declare maxSuggestions?: number;

    /**
     * Indicates the minimum number of characters required before querying for a suggestion.
     *
     * @default 1
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#minSuggestCharacters)
     */
    @JsonProperty()
    declare minSuggestCharacters?: number;

    /**
     * Specifies the fields returned with the search results.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#outFields)
     */
    @JsonProperty()
    declare outFields?: string[];

    /**
     * Used as a hint for the source input text.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#placeholder)
     */
    @JsonProperty()
    declare placeholder?: string;

    /**
     * Indicates whether to display a [Popup](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html) when a selected result is clicked.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#popupEnabled)
     */
    @JsonProperty()
    declare popupEnabled?: boolean;

    /**
     * The popup template used to display search results.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#popupTemplate)
     */
    @JsonProperty()
    declare popupTemplate?: PopupTemplateConfiguration;

    /**
     * Specify this to prefix the user's input of the search text.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#prefix)
     */
    @JsonProperty()
    declare prefix?: string;

    /**
     * Indicates whether to show a graphic on the map for the selected source using the [resultSymbol](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#resultSymbol).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#resultGraphicEnabled)
     */
    @JsonProperty()
    declare resultGraphicEnabled?: boolean;

    /**
     * The symbol used to display the result.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#resultSymbol)
     */
    @JsonProperty({ type: symbolTypePredicate })
    declare resultSymbol?: PictureMarkerSymbolConfiguration | SimpleMarkerSymbolConfiguration;

    /**
     * Specify this to add a suffix to the user's input for the search value.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#suffix)
     */
    @JsonProperty()
    declare suffix?: string;

    /**
     * Indicates whether to display suggestions as the user enters input text in the widget.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#suggestionsEnabled)
     */
    @JsonProperty()
    declare suggestionsEnabled?: boolean;

    /**
     * Indicates whether to constrain the search results to the view's extent.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#withinViewEnabled)
     */
    @JsonProperty()
    declare withinViewEnabled?: boolean;

    /**
     * The set zoom scale for the resulting search result.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchSource.html#zoomScale)
     */
    @JsonProperty()
    declare zoomScale?: number;

    constructor() {}
}

@JsonObject()
export class LocatorSearchSourceConfiguration extends SearchSourceConfiguration {
    /**
     * An authorization string used to access a resource or service.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#apiKey)
     */
    @JsonProperty()
    declare apiKey?: string;
    /**
     * A string array which limits the results to one or more categories.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#categories)
     */
    @JsonProperty()
    declare categories?: string[];

    /**
     * Constricts search results to a specified country code.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#countryCode)
     */
    @JsonProperty()
    declare countryCode?: string;

    /**
     * Sets the scale of the [MapView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#scale) or [SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html#scale) for the resulting search result, if the locator service doesn’t return an extent with a scale.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#defaultZoomScale)
     */
    @JsonProperty()
    declare defaultZoomScale: number;
    /**
     * This property controls prioritization of [Search](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html) widget result candidates depending on the view scale.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#localSearchDisabled)
     */
    @JsonProperty()
    declare localSearchDisabled?: boolean;
    /**
     * Defines the type of location, either `street` or `rooftop`, of the point returned from the [World Geocoding Service](https://developers.arcgis.com/rest/geocode/api-reference/geocoding-category-filtering.htm).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#locationType)
     */
    @JsonProperty()
    declare locationType: "rooftop" | "street";

    /**
     * The name of the source for display.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#name)
     */
    @JsonProperty()
    declare name: string;

    /**
     * A template string used to display multiple fields in a defined order when results are displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#searchTemplate)
     */
    @JsonProperty()
    declare searchTemplate: string;

    /**
     * The field name of the Single Line Address Field in the REST services directory for the locator service.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#singleLineFieldName)
     */
    @JsonProperty()
    declare singleLineFieldName: string;

    /**
     * URL to the ArcGIS Server REST resource that represents a locator service.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html#url)
     */
    @JsonProperty()
    declare url?: string;

    constructor() {
        super();
    }
}

@JsonObject()
export class LayerSearchSourceConfiguration extends SearchSourceConfiguration {
    /**
     * *Geo.js Property*
     * Query for additional data
     */
    @JsonProperty()
    declare metauid?: string;

    /**
     * *Geo.js Property*
     * If true then the query returns distinct values based on the field(s) specified in outFields.
     * https://developers.arcgis.com/javascript/latest/api-reference/esri-rest-support-Query.html#returnDistinctValues
     */
    @JsonProperty()
    declare returnDistinctValues?: boolean;
    /**
     * *Geo.js Property*
     */
    @JsonProperty()
    declare searchLayers?: Array<FeatureLayer | __esri.Sublayer | undefined>;
    /**
     * *Geo.js Property*
     */
    @JsonProperty()
    declare customQuery?: string;
    @JsonProperty()
    declare customNumericQuery?: string;
    /**
     * The results are displayed using this field. Defaults to the layer's `displayField` or the first string field.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#displayField)
     */
    @JsonProperty()
    declare displayField?: string;

    /**
     * Indicates to only return results that match the search value exactly.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#exactMatch)
     */
    @JsonProperty()
    declare exactMatch?: boolean;

    /**
     * The layer queried in the search. This is **required**.
     * > Layers created from client-side graphics are not supported.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#layer)
     */
    @JsonProperty()
    declare layer?: FeatureLayerConfiguration | __esri.FeatureLayer;

    /**
     * Id of the search layer.
     * This is the id used to
     */
    @JsonProperty()
    declare layerId: string;

    /**
     * The name of the source for display.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#name)
     */
    @JsonProperty()
    declare name?: string;
    /**
     * One or more field names used to order the query results.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#orderByFields)
     */
    @JsonProperty()
    declare orderByFields?: string[];
    /**
     * An array of string values representing the names of fields in the feature layer to search.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#searchFields)
     */
    @JsonProperty()
    declare searchFields?: string[];

    /**
     * A template string used to display multiple fields in a defined order when results are displayed.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#searchTemplate)
     */
    @JsonProperty()
    declare searchTemplate?: string;

    /**
     * A template string used to display multiple fields in a defined order when suggestions are displayed.
     * This takes precedence over `displayField`. Field names in the template must have the following format:
     * `{FieldName}`. An example suggestionTemplate could look something like: `Name: {OWNER}, Parcel: {PARCEL_ID}`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#suggestionTemplate)
     */
    @JsonProperty()
    declare suggestionTemplate?: string;

    constructor() {
        super();
    }
}

@JsonObject()
export class CustomSearchSourceConfiguration extends SearchSourceConfiguration {
    /**
     * The name of the source for display.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#name)
     */
    @JsonProperty()
    declare name?: string;

    /**
     * The layer queried in the search.
     */
    @JsonProperty()
    declare layer: __esri.FeatureLayer;

    /**
     * Id of the search layer.
     */
    @JsonProperty()
    declare layerId: string;

    /**
     * Order suggestions by field
     * Specify ASC or DESC after the field name to control order
     *
     * @example: "NUMBER ASC"
     */
    @JsonProperty()
    declare orderByFields: string[];

    constructor() {
        super();
    }
}
@JsonObject()
export class MaxExtentConfiguration {
    /**
     * Maximaler LV95 Extent
     */
    @JsonProperty()
    declare lv95?: ExtentConfiguration;
    /**
     * Maximaler WGS84 Extent
     */
    @JsonProperty()
    declare lv03?: ExtentConfiguration;
    /**
     * Maximaler LV03 Extent
     */
    @JsonProperty()
    declare wgs84?: ExtentConfiguration;

    constructor() {}
}
@JsonObject()
export class CoordinateSearchSourceConfiguration extends SearchSourceConfiguration {
    /**
     * The name of the source for display.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html#name)
     */
    @JsonProperty()
    declare maxExtent?: MaxExtentConfiguration;

    constructor() {
        super();
    }
}

@JsonObject()
export class SearchConfiguration {
    /**
     * The current active menu of the Search widget.  **Possible Values:** none | suggestion | source | warning
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#activeMenu)
     *
     * @default none
     */
    @JsonProperty()
    declare activeMenu?: string;

    /**
     * String value used as a hint for input text when searching on multiple sources. See the image below to
     * view the location and style of this text in the context of the widget.
     * ![search-allPlaceholder](https://developers.arcgis.com/javascript/assets/img/apiref/widgets/search-allplaceholder.png)
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#allPlaceholder)
     *
     * @default "Find address or place"
     */
    @JsonProperty()
    declare allPlaceholder?: string;

    /**
     * Indicates whether to automatically select and zoom to the first geocoded result. If `false`,
     * the [findAddressCandidates](https://developers.arcgis.com/rest/geocode/api-reference/geocoding-find-address-candidates.htm)
     * operation will still geocode the input string, but the top result will not be selected.
     * To work with the geocoded results, you can set up a
     * [search-complete](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#event:search-complete)
     * event handler and get the results through the event object.
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#autoSelect)
     *
     * @default true
     */
    @JsonProperty()
    declare autoSelect?: boolean;

    /**
     * The widget's default CSS icon class.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#iconClass)
     */
    @JsonProperty()
    declare iconClass?: string;
    /**
     * Indicates whether or not to include
     * [defaultSources](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchViewModel.html#defaultSources)
     * in the Search UI. This can be a boolean value or a function that returns an array of Search
     * [sources](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-SearchViewModel.html#sources).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#includeDefaultSources)
     *
     * @default true
     */
    @JsonProperty()
    declare includeDefaultSources?: boolean | Function;

    /**
     * Enables location services within the widget.
     * ![locationEnabled](https://developers.arcgis.com/javascript/assets/img/apiref/widgets/search-locationEnabled.png)
     * > The use of this property is only supported on secure origins. To use it, switch your application to a secure
     * origin, such as HTTPS. Note that localhost is considered "potentially secure" and can be used for easy testing
     * in browsers that supports [Window.isSecureContext](https://developer.mozilla.org/en-US/docs/Web/API/Window/isSecureContext#Browser_compatibility) (currently Chrome and Firefox).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#locationEnabled)
     *
     * @default true
     */
    @JsonProperty()
    declare locationEnabled?: boolean;

    /**
     * The maximum number of results returned by the widget if not specified by the source.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#maxResults)
     *
     * @default 6
     */
    @JsonProperty()
    declare maxResults?: number;

    /**
     * The maximum number of suggestions returned by the widget if not specified by the source.
     * If working with the default [ArcGIS Online Geocoding service](https://developers.arcgis.com/rest/geocode/api-reference/overview-world-geocoding-service.htm), the default remains at `5`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#maxSuggestions)
     *
     * @default 6
     */
    @JsonProperty()
    declare maxSuggestions?: number;

    /**
     * The minimum number of characters needed for the search if not specified by the source.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#minSuggestCharacters)
     *
     * @default 1
     */
    @JsonProperty()
    declare minSuggestCharacters?: number;

    /**
     * Indicates whether to display the [Popup](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html) on feature click. The graphic can be clicked to display a [Popup](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#popupEnabled)
     *
     * @default true
     */
    @JsonProperty()
    declare popupEnabled?: boolean;

    /**
     * A customized [PopupTemplate](https://developers.arcgis.com/javascript/latest/api-reference/esri-PopupTemplate.html) for the selected feature. Note that any templates defined on [allSources](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#allSources) take precedence over those defined directly on the template.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#popupTemplate)
     */
    // @JsonProperty()
    // declare popupTemplate?: PopupTemplateProperties;

    /**
     * It is possible to search a specified portal instance's [locator services](http://enterprise.arcgis.com/en/portal/latest/administer/windows/configure-portal-to-geocode-addresses.htm) Use this property to set this [ArcGIS Portal](https://enterprise.arcgis.com/en/portal/) instance to search.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#portal)
     */
    // @JsonProperty()
    // declare portal?: PortalProperties;

    /**
     * Indicates if the [resultGraphic](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#resultGraphic) will display at the location of the selected feature.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#resultGraphicEnabled)
     *
     * @default true
     */
    @JsonProperty()
    declare resultGraphicEnabled?: boolean;

    /**
     * Indicates whether to display the option to search all sources. When `true`, the "All" option is displayed by default:  ![search-searchAllEnabled-true](https://developers.arcgis.com/javascript/assets/img/apiref/widgets/search-enablesearchingall-true.png)  When `false`, no option to search all sources at once is available:  ![search-searchAllEnabled-false](https://developers.arcgis.com/javascript/assets/img/apiref/widgets/search-enablesearchingall-false.png)
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#searchAllEnabled)
     *
     * @default true
     */
    @JsonProperty()
    declare searchAllEnabled?: boolean;

    /**
     * The value of the search box input text string.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#searchTerm)
     */
    @JsonProperty()
    declare searchTerm?: string;

    /**
     * The Search widget may be used to search features in a [FeatureLayer]
     * (https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html) or [table]
     * (https://developers.arcgis.com/javascript/latest/api-reference/esri-webdoc-applicationProperties-SearchTable.html
     * ), or geocode locations with a [Locator]
     * (https://developers.arcgis.com/javascript/latest/api-reference/esri-tasks-Locator.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#sources)
     */
    @JsonProperty({ type: searchSourcePredicate })
    declare sources: SearchSourceConfiguration[];

    // /**
    //  * The Search widget may be used to search features in a
    //  * [FeatureLayer](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html) or
    //  * geocode locations with a
    //  * [Locator](https://developers.arcgis.com/javascript/latest/api-reference/esri-tasks-Locator.html).
    //  * The `sources` property defines the sources from which to search for the
    //  * [view](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#view) specified
    //  * by the Search widget instance. There are two types of sources:
    //  *   * [LocatorSearchSource](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LocatorSearchSource.html)
    //  *
    //  * Any combination of these sources may be used together in the same instance of the Search widget.
    //  * > Feature layers created from client-side graphics are not supported.
    //  *
    //  * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#sources)
    //  */
    // @JsonProperty({ type: LocatorSearchSourceConfiguration })
    // declare locatorSources?: Array<LocatorSearchSourceConfiguration>;
    //
    // /**
    //  * The Search widget may be used to search features in a
    //  * [FeatureLayer](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-FeatureLayer.html) or
    //  * geocode locations with a
    //  * [Locator](https://developers.arcgis.com/javascript/latest/api-reference/esri-tasks-Locator.html).
    //  * The `sources` property defines the sources from which to search for the
    //  * [view](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#view) specified
    //  * by the Search widget instance. There are two types of sources:
    //  *   * [LayerSearchSource](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search-LayerSearchSource.html)
    //  *
    //  * Any combination of these sources may be used together in the same instance of the Search widget.
    //  * > Feature layers created from client-side graphics are not supported.
    //  *
    //  * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#sources)
    //  */
    // @JsonProperty({ type: LayerSearchSourceConfiguration })
    // declare layerSources?: Array<LayerSearchSourceConfiguration>;

    /**
     * Enable suggestions for the widget.  This is only available if working with a 10.3 or greater geocoding service that has [suggest capability loaded](https://developers.arcgis.com/rest/geocode/api-reference/geocoding-suggest.htm) or a 10.3 or greater feature layer that supports pagination, i.e. `supportsPagination = true`.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Search.html#suggestionsEnabled)
     *
     * @default true
     */
    @JsonProperty()
    declare suggestionsEnabled?: boolean;

    /**
     * *Geo.js Property*
     * The symbols (pointer and highlight) used to display the result
     */
    @JsonProperty()
    declare resultSymbol?: SearchResultSymbolConfiguration;

    /**
     * *Geo.js Property*
     * Auf "tocReady" Status warten, bevor SearchSource initialisiert werden.
     */
    @JsonProperty()
    declare waitForTocReady?: boolean;

    constructor() {}
}
